'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { PRODUCT_FALLBACK, type VaultEntry } from '@/lib/vault';

const COLLAPSED_COUNT = 2;

interface VaultCardProps {
  entries: VaultEntry[];
}

export function VaultCard({ entries }: VaultCardProps) {
  const t = useTranslations('public');
  const [expanded, setExpanded] = useState(false);

  const visible = expanded ? entries : entries.slice(0, COLLAPSED_COUNT);
  const hasMore = entries.length > COLLAPSED_COUNT;

  return (
    <div className="pub-card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div
        className="flex justify-between items-center"
        style={{ padding: '14px 16px 10px' }}
      >
        <span
          style={{
            fontFamily: 'var(--font-pub-mono)',
            fontSize: 10,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--pub-text-muted)',
          }}
        >
          {t('home.vaultTitle')}
        </span>
        {entries.length > 0 && (
          <span
            style={{
              fontFamily: 'var(--font-pub-mono)',
              fontSize: 10,
              color: 'var(--pub-text-muted)',
              background: 'var(--pub-surface-3)',
              border: '1px solid var(--pub-border)',
              padding: '2px 8px',
              borderRadius: 100,
            }}
          >
            {t('home.vaultCount', { count: entries.length })}
          </span>
        )}
      </div>

      {/* Empty state */}
      {entries.length === 0 && (
        <div
          className="text-center"
          style={{
            padding: '16px 16px 20px',
            color: 'var(--pub-text-muted)',
            fontSize: 13,
          }}
        >
          <p>{t('home.vaultEmpty')}</p>
          <p style={{ fontSize: 11, marginTop: 4 }}>{t('home.vaultEmptySub')}</p>
        </div>
      )}

      {/* Items */}
      {visible.map((entry, i) => (
        <VaultRow key={entry.code} entry={entry} last={i === visible.length - 1 && !hasMore} />
      ))}

      {/* Expand / collapse toggle */}
      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-1.5 transition-colors duration-150"
          style={{
            padding: '10px 16px',
            borderTop: '1px solid var(--pub-border-subtle)',
            fontFamily: 'var(--font-pub-mono)',
            fontSize: 10,
            letterSpacing: '0.06em',
            color: 'var(--pub-text-muted)',
            background: 'none',
            border: 'none',
            borderTopStyle: 'solid',
            borderTopWidth: 1,
            borderTopColor: 'var(--pub-border-subtle)',
            cursor: 'pointer',
          }}
        >
          {expanded
            ? t('home.vaultCollapse')
            : t('home.vaultExpand', { count: entries.length - COLLAPSED_COUNT })}
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            width="12"
            height="12"
            style={{
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      )}
    </div>
  );
}

function VaultRow({ entry, last }: { entry: VaultEntry; last: boolean }) {
  const t = useTranslations('public');

  const handleClick = () => {
    window.location.hash = entry.code;
  };

  const serial = entry.serialNumber != null ? String(entry.serialNumber).padStart(7, '0') : null;
  const productName = entry.productName || PRODUCT_FALLBACK;

  const lastSeenFormatted = new Date(entry.lastSeenAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <button
      onClick={handleClick}
      className="w-full flex items-center gap-3 text-left transition-colors duration-150"
      style={{
        padding: '12px 16px',
        borderBottom: last ? 'none' : '1px solid var(--pub-border-subtle)',
        background: 'none',
        border: 'none',
        borderBottomStyle: last ? 'none' : 'solid',
        borderBottomWidth: last ? 0 : 1,
        borderBottomColor: 'var(--pub-border-subtle)',
        cursor: 'pointer',
      }}
    >
      {/* Product icon */}
      <div
        className="flex items-center justify-center flex-shrink-0"
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: 'linear-gradient(135deg, var(--pub-green-dim), rgba(34,197,94,0.05))',
          border: '1px solid rgba(34,197,94,0.15)',
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-green)" strokeWidth="1.8" width="18" height="18">
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12s4-3 10-3 10 3 10 3" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div
          className="truncate"
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--pub-text-primary)',
          }}
        >
          {productName}
        </div>
        <div
          className="flex items-center gap-1.5"
          style={{
            fontSize: 10,
            color: 'var(--pub-text-muted)',
            marginTop: 3,
          }}
        >
          {serial && (
            <span
              style={{
                fontFamily: 'var(--font-pub-mono)',
                letterSpacing: '0.04em',
              }}
            >
              #{serial}
            </span>
          )}
          {serial && <span style={{ opacity: 0.4 }}>·</span>}
          <span>{lastSeenFormatted}</span>
          <span
            className="flex-shrink-0"
            style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: 'var(--pub-green)',
              boxShadow: '0 0 4px var(--pub-green-glow)',
              marginLeft: 2,
            }}
          />
        </div>
      </div>

      {/* Chevron */}
      <svg viewBox="0 0 24 24" fill="none" stroke="var(--pub-text-muted)" strokeWidth="2" width="14" height="14" style={{ flexShrink: 0, opacity: 0.5 }}>
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}
